import React, { useState, useContext } from 'react';
import cn from 'classnames';
import { Info } from 'react-feather';
import request from 'utils/request_ajax';
import { INTEGRATION_OAUTH, VIDEO_CONFERENCE_TYPES } from 'constants/config';
import ZoomContext from 'components/InPersonTraining/shared/ZoomContext';
import { Button } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';

const ConnectZoomButton = ({
  currentUser,
  currentCompany,
  showTooltip = false,
  zoomOauthIntegrationId,
  disabled,
  loading = false,
}) => {
  const { zoomClientId, cacheFormState = async () => {} } =
    useContext(ZoomContext);
  const [isButtonDisabled, setButtonDisabled] = useState(disabled);

  const [showIntegrationError] = useState(
    window.location.search.includes('integrationError=true'),
  );

  const cacheUserData = () => {
    const oauthState = {
      coreRedirect: window.location.pathname,
      userId: currentUser.id,
      companyId: currentCompany.id,
      companyHost: currentCompany.host,
    };
    return request
      .postJSON('/current_user/cache', oauthState)
      .then(response => response.key);
  };

  const getOAuthStatePayload = async () => {
    const oauthStateKey = await cacheUserData();
    const formStateKey = await cacheFormState();

    let statePayload = { userId: currentUser.id, oauthStateKey };

    if (formStateKey) {
      statePayload = { ...statePayload, formStateKey };
    }

    return btoa(JSON.stringify(statePayload)); // Base64 encode the JSON string to send as state.
  };

  const startZoomOAuth = async () => {
    const integrationName = VIDEO_CONFERENCE_TYPES.ZOOM;

    setButtonDisabled(true);

    try {
      const params = {
        response_type: 'code',
        redirect_uri: INTEGRATION_OAUTH.REDIRECT_URI(
          currentCompany.host,
          integrationName,
        ),
        client_id: zoomClientId,
        state: await getOAuthStatePayload(),
      };

      const oauthParams = Object.keys(params)
        .map(key => `${key}=${params[key]}`)
        .join('&');

      window.location = INTEGRATION_OAUTH.ZOOM.AUTHORIZE(oauthParams);
    } catch (error) {
      setButtonDisabled(false);
      throw error;
    }
  };

  const IntegrationError = () => (
    <div className="d-f fd-r ai-c mb-8">
      <Info className="mr-8 llui-text-instrux-error" size={17} />
      <Text variant={TEXT_VARIANTS.INSTRUX_ERROR}>
        {t('in_person_training.new_event.connection_timed_out_error')}
      </Text>
    </div>
  );

  const tooltip = (
    <span
      className="ml-4 radio-tooltip"
      aria-label="Video Conferencing Tooltip"
      role="tooltip"
      data-toggle="tooltip"
      data-placement="top"
      title={t('in_person_training.new_event.video_conferencing_tooltip')}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex="0"
    >
      <Info size={14} color="#4a90e2" />
    </span>
  );

  const zoomButtonClassName = cn('d-ib', {
    'mb-8 ml-24': showTooltip,
  });

  return (
    <div className={zoomButtonClassName}>
      {showIntegrationError && <IntegrationError />}
      <Button
        loading={loading}
        primary={showTooltip || !zoomOauthIntegrationId}
        className="mr-4"
        onClick={() => startZoomOAuth()}
        disabled={disabled || isButtonDisabled}
      >
        {showTooltip && t('in_person_training.new_event.connect_to') + ' Zoom'}
        {!showTooltip &&
          (zoomOauthIntegrationId
            ? t('in_person_training.new_event.refresh_connection')
            : t('in_person_training.new_event.connect'))}
      </Button>
      {showTooltip && tooltip}
    </div>
  );
};

export default ConnectZoomButton;
